import React, { useEffect, useState } from "react";
import "./interactive-form.css";
import axios from "axios";

interface InteractiveFormProps {
  eventId: string;
  userEmail: string;
  userName?: string;
  isProducerView?: boolean;
  formId?: string; // collateralTypeId from the slide data
}

interface Question {
  _id: string;
  questionText: string;
  questionType: string;
  options: string[];
  order: number;
}

const InteractiveForm: React.FC<InteractiveFormProps> = ({ eventId, userEmail, userName, isProducerView, formId }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeFormId, setActiveFormId] = useState<string | null>(formId || null);
  const [formTitle, setFormTitle] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFormQuestions();
  }, [eventId, formId]);

  const fetchFormQuestions = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000/dev';
      const response = await axios.get(`${apiUrl}/v1/interactive-forms/${eventId}`);
      if (response.data && response.data.data && response.data.data.length > 0) {
        const formsList = response.data.data;
        
        if (formId) {
          // Find the specific form matching this slide's collateralTypeId
          const targetForm = formsList.find((f: any) => f._id === formId);
          if (targetForm) {
            setActiveFormId(targetForm._id);
            setFormTitle(targetForm.title || '');
            setQuestions(targetForm.questions || []);
          }
        } else {
          // Fallback: show all questions from all forms
          const allQuestions = formsList.flatMap((f: any) => f.questions || []);
          setActiveFormId(formsList[0]?._id || null);
          setFormTitle(formsList[0]?.title || '');
          setQuestions(allQuestions);
        }
      }
    } catch (error) {
      console.error("Failed to fetch interactive form questions", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (questionIndex: number, value: string, type: string) => {
    setAnswers((prev) => {
      const qAnswers = prev[questionIndex] || [];
      if (type === "CHECKBOX_MULTIPLE") {
        if (qAnswers.includes(value)) {
          return { ...prev, [questionIndex]: qAnswers.filter((v) => v !== value) };
        } else {
          return { ...prev, [questionIndex]: [...qAnswers, value] };
        }
      } else {
        // Radio or Text Input (Single value array)
        return { ...prev, [questionIndex]: [value] };
      }
    });
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length === 0) return;
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000/dev';
      
      // Formatting answers to match the backend SubmitInteractiveFormResponseDto
      const formattedAnswers = questions.map((q, index) => ({
        questionText: q.questionText,
        answerValue: answers[index] || []
      }));

      await axios.post(`${apiUrl}/v1/interactive-forms/responses`, {
        eventId,
        formId: activeFormId || 'unknown',
        userEmail,
        userName: userName || '',
        formTitle: formTitle || '',
        answers: formattedAnswers
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error("Failed to submit form responses", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="custom-modal-overlay">
        <div className="interactive-form-modal">
          <div className="success-message" style={{ color: '#aaa' }}>Loading form...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-modal-overlay">
      <div className="interactive-form-modal">
        {formTitle && (
          <div className="interactive-form-header">
            <h2>{formTitle}</h2>
          </div>
        )}
        
        <div className="interactive-form-body">
          {isSubmitted ? (
            <div className="success-message">Thank you for your response!</div>
          ) : (
            <>
              {questions.map((q, index) => (
                <div key={index} className="form-question-block">
                  <p className="question-text">{index + 1}. {q.questionText}</p>
                  
                  {q.questionType === 'TEXT_INPUT' && (
                    <input 
                      type="text" 
                      className="form-text-input" 
                      placeholder="Type your answer here..."
                      onChange={(e) => handleInputChange(index, e.target.value, q.questionType)}
                    />
                  )}

                  {(q.questionType === 'BOOLEAN_RADIO' || q.questionType === 'MULTIPLE_CHOICE_RADIO') && (
                    <div className="form-options">
                      {(q.questionType === 'BOOLEAN_RADIO' && (!q.options || q.options.length === 0)
                        ? ['Yes', 'No']
                        : q.options
                      ).map((opt, i) => (
                        <label key={i} className="form-radio-label">
                          <input 
                            type="radio" 
                            name={`question-${index}`} 
                            value={opt} 
                            onChange={() => handleInputChange(index, opt, q.questionType)}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}

                  {q.questionType === 'CHECKBOX_MULTIPLE' && (
                    <div className="form-options">
                      {q.options.map((opt, i) => (
                        <label key={i} className="form-checkbox-label">
                          <input 
                            type="checkbox" 
                            value={opt} 
                            onChange={() => handleInputChange(index, opt, q.questionType)}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {!isSubmitted && !isProducerView && (
          <div className="interactive-form-footer">
            <button 
              className="submit-btn" 
              onClick={handleSubmit} 
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Responses"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveForm;
